(() => {
    if (window.MouniCommerce) return;
    const LABELS = {
        add: 'أضف إلى السلة',
        loading: 'جاري الإضافة...',
        added: 'تمت الإضافة ✓',
        soldOut: 'انتهى من المخزون',
        unavailable: 'الخيار غير متاح',
        error: 'تعذر الإضافة'
    };

    const shopRoot = () => window.Shopify?.routes?.root || '/';
    const addUrl = () => {
        const configured = window.routes?.cart_add_url || `${shopRoot()}cart/add`;
        const url = new URL(configured, window.location.origin);
        if (!url.pathname.endsWith('.js')) url.pathname += '.js';
        return url.pathname + url.search;
    };
    const number = (value, fallback = 0) => Number.isFinite(Number(value)) ? Number(value) : fallback;

    const commerce = {
        cart: null,
        cartRequest: null,

        async getCart(force = false) {
            if (!force && this.cart) return this.cart;
            if (!force && this.cartRequest) return this.cartRequest;

            this.cartRequest = fetch(`${shopRoot()}cart.js`, {
                headers: { Accept: 'application/json' },
                credentials: 'same-origin'
            })
                .then((response) => {
                    if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
                    return response.json();
                })
                .then((cart) => {
                    this.cart = cart;
                    return cart;
                })
                .finally(() => {
                    this.cartRequest = null;
                });

            return this.cartRequest;
        },

        scope(form) {
            return form.closest('product-form, sticky-add-to-cart, [data-mouni-product]') || form.parentElement;
        },

        variants(form) {
            const script = this.scope(form)?.querySelector('script[data-mouni-variants]');
            if (!script) return [];

            if (!script._mouniVariants) {
                try {
                    script._mouniVariants = JSON.parse(script.textContent);
                } catch (error) {
                    console.error('MOUNI variant data is invalid.', error);
                    script._mouniVariants = [];
                }
            }

            return script._mouniVariants;
        },

        variant(form) {
            const id = String(form.querySelector('[name="id"]')?.value || '');
            return this.variants(form).find((variant) => String(variant.id) === id) || null;
        },

        quantityInput(form) {
            return form.querySelector('input[name="quantity"]');
        },

        quantity(form) {
            return Math.max(1, Math.floor(number(this.quantityInput(form)?.value, 1)));
        },

        cartQuantity(cart, variantId) {
            return (cart?.items || []).reduce((total, item) => {
                const itemVariantId = item.variant_id ?? item.id;
                return String(itemVariantId) === String(variantId) ? total + number(item.quantity) : total;
            }, 0);
        },

        remaining(variant, cart) {
            if (!variant?.available) return 0;
            if (!variant.inventory_management) return Number.POSITIVE_INFINITY;
            return Math.max(0, Math.max(0, number(variant.inventory_quantity)) - this.cartQuantity(cart, variant.id));
        },

        updateCartCount(itemCount) {
            if (!Number.isFinite(itemCount)) return;

            let updated = false;
            document.querySelectorAll('[data-cart-count], [data-cart-item-count], .header-cart-count, .cart-item-count, .cart-count').forEach((element) => {
                element.textContent = itemCount;
                updated = true;
            });

            document.querySelectorAll('.cart-count-bubble').forEach((bubble) => {
                const count = bubble.querySelector('[data-cart-count], [aria-hidden="true"], span');
                if (count) count.textContent = itemCount;
                bubble.classList.toggle('hidden', itemCount === 0);
                updated = true;
            });

            if (!updated && itemCount > 0) {
                const link = document.querySelector('header a[href$="/cart"], .header a[href$="/cart"], [data-cart-icon]');
                if (!link) return;
                link.classList.add('mouni-cart-link');
                let badge = link.querySelector('.mouni-cart-count-fallback');
                if (!badge) {
                    badge = document.createElement('span');
                    badge.className = 'mouni-cart-count-fallback';
                    link.append(badge);
                }
                badge.textContent = itemCount;
            }
        },

        buttonState(button, state) {
            if (!button) return;
            button.classList.remove('loading');

            if (state === 'loading') {
                button.disabled = true;
                button.setAttribute('aria-busy', 'true');
                button.textContent = button.dataset.labelLoading || LABELS.loading;
                return;
            }

            button.removeAttribute('aria-busy');
            if (state === 'added') {
                button.disabled = true;
                button.textContent = button.dataset.labelAdded || LABELS.added;
            } else if (state === 'soldout') {
                button.disabled = true;
                button.dataset.variantAvailable = 'false';
                button.textContent = button.dataset.labelSoldout || LABELS.soldOut;
            } else if (state === 'unavailable') {
                button.disabled = true;
                button.dataset.variantAvailable = 'false';
                button.textContent = button.dataset.labelUnavailable || LABELS.unavailable;
            } else if (state === 'error') {
                button.disabled = true;
                button.textContent = button.dataset.labelError || LABELS.error;
            } else {
                button.disabled = false;
                button.dataset.variantAvailable = 'true';
                button.textContent = button.dataset.labelAdd || LABELS.add;
            }
        },

        syncForm(form, cart = this.cart) {
            if (!form?.matches('[data-mouni-enabled="true"]')) return;

            const variant = this.variant(form);
            const needsChoice = form.dataset.mouniRequiresChoice === 'true' && form.dataset.mouniChoiceMade !== 'true';
            const addButton = form.querySelector('[data-mouni-atc]');
            const buyButton = form.querySelector('[data-mouni-buy-now]');
            const quantityInput = this.quantityInput(form);
            const remaining = this.remaining(variant, cart);
            const soldOut = !variant || remaining <= 0;
            const card = form.closest('mouni-product-card');
            const allSoldOut = card && !this.variants(form).some(v => v.available);
            const showSoldOut = allSoldOut || (!needsChoice && soldOut);

            if (quantityInput) {
                quantityInput.min = '1';
                quantityInput.disabled = soldOut;
                if (Number.isFinite(remaining)) {
                    quantityInput.max = String(remaining);
                    quantityInput.dataset.inventoryQuantity = String(remaining);
                    if (number(quantityInput.value, 1) > remaining) quantityInput.value = String(Math.max(1, remaining));
                } else {
                    quantityInput.removeAttribute('max');
                    delete quantityInput.dataset.inventoryQuantity;
                }

                const quantityElement = quantityInput.closest('quantity-input');
                quantityElement?.classList.toggle('disabled', soldOut);
                quantityElement?.querySelectorAll('.btn-quantity').forEach((button) => {const plus=button.matches('.plus,[name=plus]');button.disabled=soldOut || (plus ? number(quantityInput.value,1)>=remaining : number(quantityInput.value,1)<=1);button.setAttribute('aria-disabled',String(button.disabled));});
            }

            form.classList.toggle('mouni-form-sold-out', soldOut);
            if (form.dataset.mouniBusy !== 'true') {
                this.buttonState(addButton, soldOut ? (variant ? 'soldout' : 'unavailable') : 'idle');
            }

            if (needsChoice && !allSoldOut && form.dataset.mouniBusy !== 'true' && addButton) {
                addButton.disabled = true;
                addButton.textContent = form.dataset.choiceLabel || 'اختاري اللون أولاً';
            }
            if (buyButton) {
                buyButton.hidden = card ? showSoldOut : soldOut;
                buyButton.disabled = needsChoice || soldOut || form.dataset.mouniBusy === 'true';
            }
        },

        async syncAll(forceCart = false) {
            let cart = this.cart;
            try {
                cart = await this.getCart(forceCart);
            } catch (error) {
                console.error(error);
            }

            document.querySelectorAll('form[data-mouni-enabled="true"]').forEach((form) => this.syncForm(form, cart));
            if (cart) this.updateCartCount(window.MouniCartController?.summary(cart).count ?? number(cart.item_count));
            return cart;
        },

        scheduleSync(form) {
            window.clearTimeout(form._mouniSyncTimer);
            form._mouniSyncTimer = window.setTimeout(async () => {
                const cart = await this.getCart().catch(() => this.cart);
                this.syncForm(form, cart);
            }, 0);
        },

        async add(form, button, checkout = false) {
            if (!form || form.dataset.mouniBusy === 'true') return;
            if (form.dataset.mouniRequiresChoice === 'true' && form.dataset.mouniChoiceMade !== 'true') {
                this.syncForm(form); form.querySelector('[data-option-value]')?.focus(); return;
            }

            const variant = this.variant(form);
            const cart = await this.getCart().catch(() => null);
            const remaining = this.remaining(variant, cart);
            const quantity = this.quantity(form);
            const addButton = form.querySelector('[data-mouni-atc]');
            const buyButton = form.querySelector('[data-mouni-buy-now]');

            if (!variant || remaining <= 0) {
                this.syncForm(form, cart);
                return;
            }

            if (Number.isFinite(remaining) && quantity > remaining) {
                const input = this.quantityInput(form);
                if (input) input.value = String(remaining);
                this.syncForm(form, cart);
                return;
            }

            form.dataset.mouniBusy = 'true';
            if (checkout) {
                if (buyButton) buyButton.disabled = true;
                if (addButton) addButton.disabled = true;
            } else {
                this.buttonState(button || addButton, 'loading');
                if (buyButton) buyButton.disabled = true;
            }

            try {
                const payload = new FormData(form);
                payload.set('id', String(variant.id));
                payload.set('quantity', String(quantity));
                payload.set('sections', 'mouni-cart-content');
                payload.set('sections_url', window.location.pathname);

                const response = await fetch(addUrl(), {
                    method: 'POST',
                    headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                    body: payload,
                    credentials: 'same-origin'
                });
                const result = await response.json();
                if (!response.ok || result.status) throw result;

                const updatedCart = window.MouniCartController?.consumeHTML(result.sections?.['mouni-cart-content']) || await this.getCart(true);
                this.cart = updatedCart;
                this.updateCartCount(window.MouniCartController?.summary(updatedCart).count ?? number(updatedCart.item_count));
                document.dispatchEvent(new CustomEvent('mouni:cart-updated', { detail: { cart: updatedCart } }));

                if (checkout) {
                    window.location.assign(`${shopRoot()}checkout`);
                    return;
                }

                this.buttonState(button || addButton, 'added');
                window.setTimeout(() => {
                    delete form.dataset.mouniBusy;
                    this.syncAll(false);
                }, 1300);
            } catch (error) {
                console.error('MOUNI cart error:', error);
                this.cart = null;
                this.buttonState(button || addButton, 'error');
                window.setTimeout(() => {
                    delete form.dataset.mouniBusy;
                    this.syncAll(true);
                }, 1700);
            } finally {
                if (checkout && document.visibilityState === 'visible') {
                    window.setTimeout(() => {
                        delete form.dataset.mouniBusy;
                        this.syncAll(true);
                    }, 2500);
                }
            }
        },

        bindForm(form) {
            if (!form?.matches('[data-mouni-enabled="true"]') || form.dataset.mouniBound === 'true') return;
            form.dataset.mouniBound = 'true';

            form.addEventListener('submit', (event) => {
                const button = event.submitter || form.querySelector('[data-mouni-atc]');
                if (!button?.matches('[data-mouni-atc]')) return;
                event.preventDefault();
                event.stopPropagation();
                this.add(form, button, false);
            });

            form.addEventListener('click', (event) => {
                const button = event.target.closest('[data-mouni-buy-now]');
                if (!button || !form.contains(button)) return;
                event.preventDefault();
                this.add(form, button, true);
            });

            form.addEventListener('change', () => this.scheduleSync(form));
            this.scheduleSync(form);
        },

        bind(root = document) {
            root.querySelectorAll?.('form[data-mouni-enabled="true"]').forEach((form) => this.bindForm(form));
        }
    };

    window.MouniCommerce = commerce;

    class ProductForm extends HTMLElement {
        constructor() {
            super();
            this.form = this.querySelector('form');
            if (!this.form) return;

            if (this.form.matches('[data-mouni-enabled="true"]')) {
                commerce.bindForm(this.form);
                return;
            }

            this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
            this.cartNotification = document.querySelector('cart-notification');
        }

        onSubmitHandler(event) {
            event.preventDefault();
            this.cartNotification?.setActiveElement(document.activeElement);
            const submitButton = this.querySelector('[type="submit"]');
            if (!submitButton || submitButton.disabled) return;

            submitButton.disabled = true;
            submitButton.classList.add('loading');
            const sections = this.cartNotification?.getSectionsToRender?.() || [];
            const requestData = JSON.parse(serializeForm(this.form));
            if (sections.length) {
                requestData.sections = sections.map((section) => section.id);
                requestData.sections_url = window.location.pathname;
            }

            fetch(window.routes?.cart_add_url || '/cart/add', {
                ...fetchConfig('javascript'),
                body: JSON.stringify(requestData)
            })
                .then((response) => response.json())
                .then((state) => this.cartNotification?.renderContents(state))
                .catch((error) => console.error(error))
                .finally(() => {
                    submitButton.classList.remove('loading');
                    submitButton.disabled = false;
                });
        }
    }

    if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);

    const boot = () => {
        commerce.bind(document);
        commerce.syncAll(true);
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', boot, { once: true });
    } else {
        boot();
    }

    document.addEventListener('shopify:section:load', (event) => {
        commerce.bind(event.target);
        commerce.syncAll(true);
    });
})();
